import {
  FEATURE_FLAGS,
  FEATURES,
  hasPermission,
  isNotificationsManagerVisible,
  PERMISSION_ACTIONS,
  PERMISSION_KEYS,
  PermissionsUtils,
  ROUTES,
  snakeToHumanCase,
  EngagementTypeUtils,
  UserUtils
} from '@cyberint/common-utils';
import { Icons } from '@cyberint/dsc';
import { t } from '../i18n';
import * as ACCESS_ROLES from './access-roles';
import * as AUTH_UTILS from './auth-utils';
import * as BADGE_TYPES from './badge-types';
import * as IS_USER_HAVE_ACCESS_TO_TAB from './is-user-have-access-to-tab';
import * as NavKeywords from './navigation-search-keywords';
import { userHasAccessRole } from './user-has-access-role';

const {
  COLLABORATION_REPORTS,
  INBOX_FEATURE_ACCESS,
  ASM_FEATURE_VIEW,
  NEWS_TICKER_ACCESS,
  CVE_ACCESS,
  ALERTS_VIEW,
  TIP_ACCESS,
  VIEW_EXECUTIVE_DASHBOARDS,
  ENV_SUMMARY_FEATURE_VIEW,
  TAKEDOWN_SUBMIT,
  SOCIAL_MEDIA_FAKE_PAGES: SOCIAL_MEDIA_FAKE_PAGES_FLAG,
  DOMAIN_SQUATTING_COLLECTION,
  SUPPLY_CHAIN_VENDOR_ACCESS,
  CREDENTIAL_PURCHASE_ACCESS,
  COMPROMISED_CREDENTIALS_ACCESS,
  API_LEAKED_CREDENTIALS_BROAD_QUERY,
  FEEDS_CREDENTIALS,
  INTELLIGENCE_ARCHIVE_VIEW,
  GLOBAL_SEARCH,
  INTELLIGENCE_IOC_PREVENTION,
  INTELLIGENCE_IOC_DETECTION,
  INTELLIGENCE_IOC_FULL_ACCESS
} = FEATURES;

const {
  TI_EVENTS_TAB,
  FORENSICS_TAB,
  REPORT_TAB,
  DIGITAL_FOOTPRINT_TAB,
  TELEGRAM_TAB,
  DISCORD_TAB,
  THREAD_VIEW_TAB,
  KNOWLEDGE_BASE_TAB,
  ACCOUNT,
  ALERTS_TAB,
  OPERATIONAL_DASHBOARD_TAB,
  EXECUTIVE_DASHBOARD_TAB,
  NEWS_TICKER_TAB,
  INTELLIGENCE_ARCHIVE_TAB,
  ENV_SUMMARY_TAB,
  AUTOMATED_TAKEDOWN_TAB,
  USER_SETTINGS_TAB,
  USER_MANAGEMENT,
  MY_PROFILE,
  SOCIAL_MEDIA_FAKE_PAGES_TAB,
  LOOKALIKE_DOMAIN_TAB,
  CVE_TAB,
  IOC_MGMT_TAB,
  THREAT_READINESS_TAB,
  CYBERINT_NEWS_TAB,
  CYBER_NEWS_TAB,
  VENDOR_INVENTORY_TAB,
  MALWARE_KB_TAB,
  SEARCH_TAB,
  GLOBAL_SEARCH_TAB,
  CUSTOMER_TAB,
  INDICATOR_TAB,
  ENTITY_TAB,
  CVE_INDICATOR_TAB,
  SUPPLY_CHAIN_INDICATOR_TAB,
  NEWS_INDICATOR_TAB,
  ASM_REVAMP_TAB,
  INVESTIGATIONS_TAB,
  LEAKED_CREDENTIALS_TAB,
  INCIDENTS_TAB,
  THREAT_ACTORS_TAB,
  THREATCLOUD_FEED_TAB,
  CREDENTIAL_PURCHASED_TAB,
  WORKBENCH_TAB,
  IMPACTOMETER,
  RISK_DASHBOARD_TAB,
  CAMPAIGNS_TAB,
  STRATEGIC_INTELLIGENCE,
  KNOWLEDGE_HUB_TAB,
  CYBER_NEWS_ATTACKS_TAB
} = ROUTES;

const { READ, WRITE } = PERMISSION_ACTIONS;

const { ALL_DOMAINS, TEM_DOMAINS, CAASM_DOMAINS } = ROUTES;

/**
 * Tab Object Schema
 *   title: The title of the page,
 *   path: The route of the page`,
 *   type: The ID of the tab,
 *   hideTenantSelection: if true - will not show tenant selection in breadcrumbs (default is false),
 *   subPages: this is used to highlight the route in the menu if we actually shows another route...for example, old ASM is sub-route of AMS-NG
 *   useParentOrChildrenFeatures: if true, the page will be visible if the logged in tenant's features or ony of the children have the specified features.
 *                                This flag uses the features_access value and precedes the useParentFeatures and the predicate function.
 *   predicate: decision action that returns true or false if to show/hide the page/tab/menu.
 *   useParentFeatures: if true, we will decide the visibility of the page dependent on the logged in tenant's features and not the selected one.
 *   features_access: array of features that if at least one is enabled, it will be visible,
 *   predicate: evaluated with feature flags
 *   isVisible: evaluated before all conditions
 *   keywords: search aliases for this tab (used by navigation search to match beyond the display title),
 */

// allows us to check this main tab is active given a URL.
export const MY_ACCOUNT = {
  title: t('common.ermSettings'),
  path: `/${ACCOUNT}`,
  pathPattern: `/${ACCOUNT}/*`,
  type: ACCOUNT,
  newTab: true,
  domains: ALL_DOMAINS,
  predicate: PermissionsUtils.isAccountSettingsAllowed,
  keywords: NavKeywords.myAccount
};

export const NEWS_TICKER = {
  title: t('common.security_news'),
  path: `/${NEWS_TICKER_TAB}`,
  type: NEWS_TICKER_TAB,
  hideTenantSelection: true,
  useParentFeatures: true,
  features_access: [NEWS_TICKER_ACCESS],
  keywords: NavKeywords.newsTicker
};

export const LOOKALIKE_DOMAIN = {
  title: t('common.lookalike-domain'),
  path: `/${LOOKALIKE_DOMAIN_TAB}`,
  type: LOOKALIKE_DOMAIN_TAB,
  features_access: [DOMAIN_SQUATTING_COLLECTION],
  keywords: NavKeywords.lookalikeDomain
};

export const CVE_INDICATOR = {
  title: t('tabs.indicator'),
  path: `/${CVE_INDICATOR_TAB}`,
  pathPattern: `/${CVE_INDICATOR_TAB}/:indicatorId`,
  type: CVE_INDICATOR_TAB
};

// Legacy CVE module — superseded by CVE_NG (which now serves the /cve route and "CVE" title).
// Kept for reference/rollback; hidden from navigation. Removal tracked as a follow-up.
export const CVE = {
  title: t('common.cve'),
  path: `/${CVE_TAB}`,
  type: CVE_TAB,
  useParentFeatures: true,
  features_access: [CVE_ACCESS],
  subPages: [CVE_INDICATOR],
  keywords: NavKeywords.cve,
  isVisible: () => false
};

export const MALWARE_KB_PAGE = {
  title: t('common.malware-intel'),
  path: `/${MALWARE_KB_TAB}`,
  pathPattern: `/${MALWARE_KB_TAB}/*`,
  type: MALWARE_KB_TAB,
  keywords: NavKeywords.malwareKb
};

export const THREAT_ACTORS_PAGE = {
  title: t('common.threat_actors'),
  path: `/${THREAT_ACTORS_TAB}`,
  pathPattern: `/${THREAT_ACTORS_TAB}/*`,
  type: THREAT_ACTORS_TAB,
  keywords: NavKeywords.threatActors
};

export const CAMPAIGNS = {
  title: t('common.campaigns'),
  path: `/${CAMPAIGNS_TAB}`,
  pathPattern: `/${CAMPAIGNS_TAB}/*`,
  type: CAMPAIGNS_TAB,
  badgeType: BADGE_TYPES.NEW,
  keywords: NavKeywords.campaigns
};

// CVE module (new generation). Now the active CVE tab — takes the legacy "CVE" title and /cve route.
export const CVE_NG = {
  title: t('common.cve'),
  path: `/${CVE_TAB}`,
  pathPattern: `/${CVE_TAB}/*`,
  type: CVE_TAB,
  badgeType: BADGE_TYPES.NEW,
  useParentFeatures: true,
  features_access: [CVE_ACCESS],
  subPages: [CVE_INDICATOR],
  keywords: NavKeywords.cveNg
};

export const IOC_MGMT = {
  title: t('common.iocManagement'),
  path: `/${IOC_MGMT_TAB}`,
  pathPattern: `/${IOC_MGMT_TAB}/*`,
  type: IOC_MGMT_TAB,
  badgeType: BADGE_TYPES.BETA,
  hideTenantSelection: false,
  features_access: [TIP_ACCESS],
  keywords: NavKeywords.iocExplorer
};

export const THREAT_READINESS_DASHBOARD = {
  title: t('common.threatReadinessDashboard'),
  path: `/${THREAT_READINESS_TAB}`,
  pathPattern: `/${THREAT_READINESS_TAB}/*`,
  type: THREAT_READINESS_TAB,
  badgeType: BADGE_TYPES.NEW,
  hideTenantSelection: false,
  isVisible: ({ user, selectedTenant }) => {
    const features = selectedTenant?.features || user?.customer?.features;
    return PermissionsUtils.isThreatReadinessDashboardVisible && PermissionsUtils.isTemAllowed({ features });
  },
  keywords: NavKeywords.threatReadinessDashboard
};

export const THREAT_LANDSCAPE = {
  title: t('common.threat_landscape'),
  path: `/${WORKBENCH_TAB}`,
  type: WORKBENCH_TAB,
  keywords: NavKeywords.threatLandscape
};

export const CYBERINT_NEWS_PAGE = {
  title: t('common.cyber_news_attacks'),
  path: `/${CYBERINT_NEWS_TAB}`,
  type: CYBERINT_NEWS_TAB,
  hideTenantSelection: true,
  keywords: NavKeywords.cyberintNews
};

export const CYBER_NEWS_PAGE = {
  title: t('common.security_news'),
  path: `/${CYBER_NEWS_TAB}`,
  type: CYBER_NEWS_TAB,
  features_access: [NEWS_TICKER_ACCESS],
  hideTenantSelection: true,
  keywords: NavKeywords.cyberintNews,
  isVisible: PermissionsUtils.isCyberNewsVisible
};

export const KNOWLEDGE_BASE = {
  title: t('common.knowledge-base'),
  path: `/${KNOWLEDGE_BASE_TAB}`,
  type: KNOWLEDGE_BASE_TAB,
  predicate: ({ user }) => UserUtils.isCyberintRootUser(user)
};

export const NOTIFICATIONS_MANAGER = {
  title: 'Notifications Manager',
  path: `/${ROUTES.NOTIFICATIONS_MANAGER_TAB}`,
  type: ROUTES.NOTIFICATIONS_MANAGER_TAB,
  hideTenantSelection: true,
  badgeType: BADGE_TYPES.BETA,
  isVisible: ({ user }) => isNotificationsManagerVisible() && UserUtils.isCyberintRootUser(user)
};

export const THREATCLOUD_FEED = {
  title: t('common.threatcloud_feed'),
  path: `/${THREATCLOUD_FEED_TAB}`,
  pathPattern: `/${THREATCLOUD_FEED_TAB}/*`,
  type: THREATCLOUD_FEED_TAB,
  keywords: NavKeywords.threatcloudFeed,
  features_access: [INTELLIGENCE_IOC_PREVENTION, INTELLIGENCE_IOC_DETECTION, INTELLIGENCE_IOC_FULL_ACCESS]
};

export const KNOWLEDGE_HUB = {
  title: t('common.knowledge_hub'),
  path: `/${THREAT_ACTORS_TAB}`,
  type: KNOWLEDGE_HUB_TAB,
  hideTenantSelection: true,
  subPages: [THREAT_ACTORS_PAGE, CVE_NG, MALWARE_KB_PAGE, THREATCLOUD_FEED],
  keywords: NavKeywords.knowledgeHub
};

export const INCIDENTS = {
  title: t('common.incidents'),
  path: `/${INCIDENTS_TAB}`,
  pathPattern: `/${INCIDENTS_TAB}/*`,
  type: INCIDENTS_TAB,
  keywords: NavKeywords.incidents
};

export const CYBER_NEWS_ATTACKS = {
  title: t('common.cyber_news_attacks'),
  path: `/${INCIDENTS_TAB}`,
  type: CYBER_NEWS_ATTACKS_TAB,
  hideTenantSelection: true,
  subPages: [INCIDENTS, CAMPAIGNS, CYBERINT_NEWS_PAGE, NEWS_TICKER],
  keywords: NavKeywords.cyberNewsAttacks
};

export const LICENSE_MGMT = {
  title: t('my-account.license-management'),
  path: `/${ROUTES.LICENSE_MANAGEMENT}`,
  type: ROUTES.LICENSE_MANAGEMENT,
  hideTenantSelection: true,
  domains: ALL_DOMAINS,
  predicate: PermissionsUtils.isLicenseVisibilityAllowed
};

export const INDICATOR = {
  title: t('tabs.indicator'),
  path: `/${INDICATOR_TAB}`,
  pathPattern: `/${INDICATOR_TAB}/:indicatorId`,
  type: INDICATOR_TAB
};

export const SUPPLY_CHAIN_INDICATOR = {
  title: t('tabs.indicator'),
  path: `/${SUPPLY_CHAIN_INDICATOR_TAB}`,
  pathPattern: `/${SUPPLY_CHAIN_INDICATOR_TAB}/:indicatorId`,
  type: SUPPLY_CHAIN_INDICATOR_TAB
};

export const NEWS_INDICATOR = {
  title: t('tabs.indicator'),
  path: `/${NEWS_INDICATOR_TAB}`,
  pathPattern: `/${NEWS_INDICATOR_TAB}/:indicatorId`,
  type: NEWS_INDICATOR_TAB
};

// Inbox has been renamed to TI Events by text display!
export const INBOX = {
  title: t('common.darkweb_raw_intelligence'),
  path: `/${TI_EVENTS_TAB}`,
  type: TI_EVENTS_TAB,
  subPages: [INDICATOR, CVE_INDICATOR, SUPPLY_CHAIN_INDICATOR, NEWS_INDICATOR],
  features_access: INBOX_FEATURE_ACCESS,
  keywords: NavKeywords.tiEvents
};

export const INTELLIGENCE_ARCHIVE = {
  title: t('common.intelligence_data_lake'),
  path: `/${INTELLIGENCE_ARCHIVE_TAB}`,
  type: INTELLIGENCE_ARCHIVE_TAB,
  hideTenantSelection: true,
  useParentFeatures: true,
  predicate: ({ permissions }) => hasPermission({ permissions, key: PERMISSION_KEYS.DATA_LAKE, action: READ }),
  keywords: NavKeywords.intelligenceArchive
};

export const FORENSICS = {
  title: t('common.forensic'),
  path: `/${FORENSICS_TAB}`,
  type: FORENSICS_TAB,
  useParentFeatures: true,
  predicate: ({ permissions }) => hasPermission({ permissions, key: PERMISSION_KEYS.FORENSIC, action: WRITE }),
  keywords: NavKeywords.forensics
};

export const REPORTS = {
  title: t('common.reports'),
  path: `/${REPORT_TAB}`,
  type: REPORT_TAB,
  olhTopic: 'Reports',
  domains: ALL_DOMAINS,
  features_access: [COLLABORATION_REPORTS],
  keywords: NavKeywords.reports
};

export const DIGITAL_FOOTPRINT = {
  title: t('common.attack-surface-monitoring'),
  path: `/${DIGITAL_FOOTPRINT_TAB}`,
  pathPattern: `/${DIGITAL_FOOTPRINT_TAB}/*`,
  type: DIGITAL_FOOTPRINT_TAB,
  icon: Icons.Radar,
  features_access: [ASM_FEATURE_VIEW]
};
export const ASM_PAGE = DIGITAL_FOOTPRINT;

export const STRATEGIC_INTELLIGENCE_PAGE = {
  title: t('common.strategic_intelligence'),
  path: `/${STRATEGIC_INTELLIGENCE}`,
  pathPattern: `/${STRATEGIC_INTELLIGENCE}/*`,
  type: STRATEGIC_INTELLIGENCE,
  // TODO: itzik add relevant FF
  features_access: [ALERTS_VIEW],
  keywords: NavKeywords.strategicIntelligence
};

export const ALERTS = {
  title: t('common.alertsCenter'),
  path: `/${ALERTS_TAB}`,
  pathPattern: `/${ALERTS_TAB}/*`,
  type: ALERTS_TAB,
  features_access: [ALERTS_VIEW],
  subPages: [STRATEGIC_INTELLIGENCE_PAGE],
  keywords: NavKeywords.alerts
};

export const OPERATIONAL_DASHBOARD = {
  title: t('common.operationalDashboard'),
  path: `/${OPERATIONAL_DASHBOARD_TAB}`,
  type: OPERATIONAL_DASHBOARD_TAB,
  icon: Icons.Home,
  features_access: [ALERTS_VIEW],
  keywords: NavKeywords.operationalDashboard
};

export const EXECUTIVE_DASHBOARD = {
  title: t('common.executiveDashboard'),
  path: `/${EXECUTIVE_DASHBOARD_TAB}`,
  type: EXECUTIVE_DASHBOARD_TAB,
  features_access: [ALERTS_VIEW, VIEW_EXECUTIVE_DASHBOARDS],
  keywords: NavKeywords.executiveDashboard
};

export const RISK_DASHBOARD = {
  title: t('common.external_risk_dashboard'),
  path: `/${RISK_DASHBOARD_TAB}`,
  type: RISK_DASHBOARD_TAB,
  keywords: NavKeywords.riskDashboard
};

export const ENV_SUMMARY = {
  title: t('common.env_summary'),
  path: `/${ENV_SUMMARY_TAB}`,
  type: ENV_SUMMARY_TAB,
  hideTenantSelection: true,
  useParentFeatures: true,
  isVisible: ({ user }) =>
    !userHasAccessRole(user, ACCESS_ROLES.ROLE_CYBERINT) &&
    AUTH_UTILS.hasFeatureAccess(user?.customer?.features, [ENV_SUMMARY_FEATURE_VIEW]),
  keywords: NavKeywords.envSummary
};

export const AUTOMATED_TAKEDOWN = {
  title: t('common.takedowns'),
  path: `/${AUTOMATED_TAKEDOWN_TAB}`,
  pathPattern: `/${AUTOMATED_TAKEDOWN_TAB}/*`,
  type: AUTOMATED_TAKEDOWN_TAB,
  features_access: [TAKEDOWN_SUBMIT],
  keywords: NavKeywords.automatedTakedown
};

export const CREDENTIAL_PURCHASED = {
  title: t('common.credential_purchases'),
  path: `/${CREDENTIAL_PURCHASED_TAB}`,
  type: CREDENTIAL_PURCHASED_TAB,
  hideTenantSelection: true,
  features_access: [CREDENTIAL_PURCHASE_ACCESS],
  useParentFeatures: true,
  keywords: NavKeywords.credentialPurchased
};

export const SOCIAL_MEDIA_FAKE_PAGES = {
  title: t('common.social_media_impersonation'),
  path: `/${SOCIAL_MEDIA_FAKE_PAGES_TAB}`,
  type: SOCIAL_MEDIA_FAKE_PAGES_TAB,
  useParentOrChildrenFeatures: true,
  features_access: [SOCIAL_MEDIA_FAKE_PAGES_FLAG],
  keywords: NavKeywords.socialMediaFakePages
};

export const USER_SETTINGS = {
  title: t('common.user_settings'),
  path: `/${ACCOUNT}/${USER_MANAGEMENT}/${MY_PROFILE}`,
  type: USER_SETTINGS_TAB
};

export const USERS = {
  title: t('my-account.user-management'),
  path: `/${ROUTES.USERS}`,
  type: ROUTES.USERS,
  domains: ALL_DOMAINS,
  predicate: ({ permissions }) => hasPermission({ permissions, key: PERMISSION_KEYS.USERS, action: WRITE })
};

export const TELEGRAM = {
  title: t('common.telegram-channel-view'),
  path: `/${TELEGRAM_TAB}`,
  pathPattern: `/${TELEGRAM_TAB}/:channelId/:messageId/:parentId/:contentType`,
  type: TELEGRAM_TAB,
  newTab: true
};

export const DISCORD = {
  title: t('common.discord-channel-view'),
  path: `/${DISCORD_TAB}`,
  pathPattern: `/${DISCORD_TAB}/:channelId/:messageId/:parentId/:contentType`,
  type: DISCORD_TAB,
  newTab: true
};

export const IMPACTOMETER_TAB = {
  title: t('common.impactometer'),
  path: `/${IMPACTOMETER}/*`,
  type: IMPACTOMETER
};

/* BLAST is a two-entry group under one B icon (see EM_MENU): Findings (viewer-readable) and
 * Scans (tenant-manager surfaces). Both entries share the single BLAST_VISIBILITY flag.
 * Scans is NOT additionally manager-gated here — not because it couldn't be (`isVisible` receives
 * `user` and `permissions`; only the `predicate` FIELD is unusable, since `checkAccessToTab`
 * returns early on a function `isVisible`), but because the shell has no signal for
 * blast-service's MANAGER_ROLE: that is an identity-token role, whereas ERM offers `role_id`
 * (Argos ADMIN) and permission keys with no BLAST key among them. Mapping ADMIN onto "manager"
 * would hide a working page from real BLAST managers, so the remote gates itself instead
 * (non-managers get a full-page "Scans is for tenant managers" panel). */
export const BLAST_TAB = {
  title: t('common.blastFindings'),
  path: ROUTES.BLAST_TAB,
  pathPattern: `/${ROUTES.BLAST_TAB}/*`,
  type: ROUTES.BLAST_TAB,
  isVisible: ({ flags }) => flags?.[FEATURE_FLAGS.BLAST_VISIBILITY]
};

export const BLAST_SCANS_TAB = {
  title: t('common.blastScans'),
  path: ROUTES.BLAST_SCANS_TAB,
  pathPattern: `/${ROUTES.BLAST_SCANS_TAB}/*`,
  type: ROUTES.BLAST_SCANS_TAB,
  isVisible: ({ flags }) => flags?.[FEATURE_FLAGS.BLAST_VISIBILITY]
};

export const THREAD_VIEW = {
  title: t('common.thread-channel-view'),
  path: `/${THREAD_VIEW_TAB}`,
  pathPattern: `/${THREAD_VIEW_TAB}/:platform/:channelId/:messageId/:parentId/:contentType`,
  type: THREAD_VIEW_TAB,
  newTab: true
};

export const VENDOR_INVENTORY = {
  title: t('common.supply_chain_intelligence'),
  path: `/${VENDOR_INVENTORY_TAB}`,
  pathPattern: `/${VENDOR_INVENTORY_TAB}/*`,
  type: VENDOR_INVENTORY_TAB,
  features_access: [SUPPLY_CHAIN_VENDOR_ACCESS],
  keywords: NavKeywords.vendorInventory
};
export const ASM_REVAMP = {
  title: t('common.external_attack_surface'),
  path: `/${ASM_REVAMP_TAB}`,
  pathPattern: `/${ASM_REVAMP_TAB}/*`,
  type: ASM_REVAMP_TAB,
  subPages: [ASM_PAGE],
  features_access: [ASM_FEATURE_VIEW],
  /* moved from isVisible: PermissionsUtils.isASMVisible to simple predicate
   * as the isASMVisible return true for cyberint user, to it's left to check regular FF and rest of condition. */
  predicate: PermissionsUtils.isASMVisible,
  keywords: NavKeywords.asmRevamp
};

export const INVESTIGATIONS = {
  title: t('common.analyst_investigations'),
  path: `/${INVESTIGATIONS_TAB}`,
  pathPattern: `/${INVESTIGATIONS_TAB}/*`,
  type: INVESTIGATIONS_TAB,
  useParentOrChildrenFeatures: true,
  features_access: [FEATURES.INVESTIGATION_ACCESS],
  keywords: NavKeywords.investigations
};

export const LEAKED_CREDENTIALS = {
  title: t('common.leaked_credentials'),
  path: `/${LEAKED_CREDENTIALS_TAB}`,
  pathPattern: `/${LEAKED_CREDENTIALS_TAB}/*`,
  type: LEAKED_CREDENTIALS_TAB,
  useParentFeatures: true,
  features_access: [COMPROMISED_CREDENTIALS_ACCESS],
  keywords: NavKeywords.leakedCredentials
};

export const THREAT_EMULATION = {
  title: t('common.threats_emulation'),
  path: `/${ROUTES.THREAT_EMULATION_TAB}`,
  pathPattern: `/${ROUTES.THREAT_EMULATION_TAB}/*`,
  type: ROUTES.THREAT_EMULATION_TAB,
  predicate: ({ user }) => UserUtils.isThreatHuntingUser(user),
  keywords: NavKeywords.filesAnalyzer
};

export const AUDIT_LOG = {
  title: t('common.auditLog'),
  path: `/${ROUTES.AUDIT_LOG}`,
  pathPattern: `/${ROUTES.AUDIT_LOG}/*`,
  type: ROUTES.AUDIT_LOG,
  hideTenantSelection: true,
  predicate: ({ user }) =>
    UserUtils.isAdminUser(user) || (UserUtils.isCyberintUser(user) && UserUtils.isProducerUser(user))
};

export const CUSTOMER_MGMT = {
  title: t('my-account.customers'),
  path: `/${CUSTOMER_TAB}`,
  pathPattern: `/${CUSTOMER_TAB}/:customerId/*`,
  type: CUSTOMER_TAB,
  hideTenantSelection: true,
  domains: ALL_DOMAINS,
  // Hide Tenants tab for MT-management admin if FF app.multi_tenant_tenant_type.enable is off
  predicate: ({ user, flags, permissions }) => {
    if (UserUtils.isMultiTenantMgmtAdmin(user) && !flags?.[FEATURE_FLAGS.MULTI_TENANT_TENANT_TYPE]) {
      return false;
    }
    return hasPermission({ permissions, key: PERMISSION_KEYS.TENANTS, action: PERMISSION_ACTIONS.READ });
  },
  keywords: NavKeywords.customerMgmt
};

export const GLOBAL_SEARCH_PAGE = {
  title: t('tabs.global_search'),
  path: `/${GLOBAL_SEARCH_TAB}`,
  type: GLOBAL_SEARCH_TAB,
  hideTenantSelection: true,
  useParentFeatures: true
}; // Knowledge Base Global Search Page

export const SEARCH_PAGE = {
  title: t('tabs.search'),
  path: `/${SEARCH_TAB}`,
  pathPattern: `/${SEARCH_TAB}/:query`,
  type: SEARCH_TAB,
  hideTenantSelection: true,
  useParentFeatures: true,
  features_access: [API_LEAKED_CREDENTIALS_BROAD_QUERY, FEEDS_CREDENTIALS, INTELLIGENCE_ARCHIVE_VIEW, GLOBAL_SEARCH]
}; // Legacy Search Page

export const ENTITY_PAGE = {
  title: t('tabs.entity'),
  path: `/${ENTITY_TAB}`,
  pathPattern: `/${ENTITY_TAB}/:entityId/*`,
  type: ENTITY_TAB,
  hideTenantSelection: true,
  useParentFeatures: true
};

export const temTitles = {
  map: t('common.live_org_attack_map'),
  dashboard: t('common.internal_exposure_overview'),
  settings: t('common.temSettings'),
  indicators: t('common.ioc_management'),
  insights: t('common.security_insights'),
  integrations: t('common.tem_integrations'),
  assets: t('common.security_controls'),
  'vulnerability-remediation': t('common.vulnerability_prioritization')
};
const createTemTab = type => ({
  title: temTitles[type] || snakeToHumanCase(type),
  path: `/${ROUTES.TEM}/${type}`,
  pathPattern: `/${ROUTES.TEM}/${type}/*`,
  type,
  domains: TEM_DOMAINS,
  features_access: [FEATURES.TEM_ACCESS],
  predicate: PermissionsUtils.isTemAllowed
});
export const TEM_INTEGRATIONS = { ...createTemTab('integrations'), keywords: NavKeywords.temIntegrations };
export const TEM_DASHBOARD = { ...createTemTab('dashboard'), keywords: NavKeywords.temDashboard };
export const TEM_MAP = {
  ...createTemTab('map'),
  features_access: [FEATURES.TEM_ACCESS, FEATURES.TEM_LIVE_ATTACK_MAP],
  allFeaturesAccess: true,
  keywords: NavKeywords.temMap
};
export const TEM_INDICATORS = { ...createTemTab('indicators'), keywords: NavKeywords.temIndicators };
export const TEM_REMEDIATIONS = {
  ...createTemTab('vulnerability-remediation'),
  keywords: NavKeywords.temRemediation
};
// Security Controls is hidden for TEM Essentials tenants (hotfix — no feature distinguishes it)
export const TEM_ASSETS = {
  ...createTemTab('assets'),
  keywords: NavKeywords.temAssets,
  predicate: ({ selectedTenant, features }) =>
    // Security Controls page must be hidden from tenant with tem_essentials engagement type
    PermissionsUtils.isTemAllowed({ features }) && !EngagementTypeUtils.isTemEssentials(selectedTenant)
};
export const TEM_INSIGHTS = { ...createTemTab('insights'), keywords: NavKeywords.temInsights };
export const TEM_SETTINGS = { ...createTemTab('settings'), keywords: NavKeywords.temSettings };

export const cyclopsTitles = {
  'caasm-data-enrichment': t('cyclops.data-enrichment'),
  'caasm-email-query': t('cyclops.email-query'),
  'caasm-risk-scores': t('cyclops.risk-scores'),
  'caasm-risk-cards': t('cyclops.risk-cards'),
  'caasm-saved-query': t('cyclops.saved-query'),
  'caasm-search': t('cyclops.search'),
  'caasm-dashboards': t('cyclops.dashboards'),
  'caasm-integrations': t('cyclops.integrations'),
  [ROUTES.ASM_INTEGRATIONS]: t('cyclops.asm_integrations'),
  [ROUTES.ERM_INTEGRATIONS]: t('cyclops.erm_integrations'),
  'caasm-internal-asm': t('cyclops.internal-asm'),
  'caasm-configuration': t('cyclops.configuration')
};
const createCyclopsTab = type => ({
  title: cyclopsTitles[type] || snakeToHumanCase(type),
  path: `/${ROUTES.CYCLOPS}/${type}`,
  pathPattern: `/${ROUTES.CYCLOPS}/${type}/*`,
  type,
  keywords: NavKeywords.cyclops,
  domains: CAASM_DOMAINS,
  isVisible: ({ permissions }) => hasPermission({ permissions, key: PERMISSION_KEYS.CYCLOPS, action: READ })
  // features_access: [FEATURES.ASM_FEATURE_ACCESS]
});

export const CYCLOPS_SEARCH = createCyclopsTab('caasm-search');
export const CYCLOPS_DASHBOARDS = createCyclopsTab('caasm-dashboards');
export const CYCLOPS_INTEGRATIONS = createCyclopsTab(ROUTES.CYCLOPS_INTEGRATIONS);
export const ASM_INTEGRATIONS = {
  ...createCyclopsTab(ROUTES.ASM_INTEGRATIONS),
  domains: undefined,
  isVisible: ({ permissions }) =>
    hasPermission({ permissions, key: PERMISSION_KEYS.CAASM_ASM_INTEGRATION, action: READ })
};
export const CYCLOPS_INTERNAL_ASM = createCyclopsTab('caasm-internal-asm');
export const CYCLOPS_RISK_SCORES = createCyclopsTab('caasm-risk-scores');
export const CYCLOPS_RISK_CARDS = {
  ...createCyclopsTab('caasm-risk-cards'),
  isVisible: ({ permissions, flags }) =>
    !!flags?.[FEATURE_FLAGS.CAASM_WIDGET_CDN] &&
    hasPermission({ permissions, key: PERMISSION_KEYS.CYCLOPS, action: READ })
};
export const CYCLOPS_DATA_ENRICHMENT = createCyclopsTab('caasm-data-enrichment');
export const CYCLOPS_QUERIES = createCyclopsTab('caasm-saved-query');
export const CYCLOPS_EMAIL_QUERY = createCyclopsTab('caasm-email-query');

export const CYCLOPS_CONFIGURATION = {
  ...createCyclopsTab('caasm-configuration'),
  path: CYCLOPS_RISK_SCORES.path,
  subPages: [CYCLOPS_RISK_SCORES, CYCLOPS_DATA_ENRICHMENT, CYCLOPS_QUERIES, CYCLOPS_RISK_CARDS]
};

// ERM integrations: the catalog (a Cyclops widget page) and the alert delivery pages
// moved here from Account Settings -> Targeted Alerts
const isErmIntegrationsAllowed = ctx =>
  PermissionsUtils.isAccountSettingsAllowed(ctx) &&
  PermissionsUtils.isAssetReadAllowed({ customer: ctx.selectedTenant, user: ctx.user });
export const ERM_INTEGRATIONS = {
  title: cyclopsTitles[ROUTES.ERM_INTEGRATIONS],
  path: `/${ROUTES.CYCLOPS}/${ROUTES.ERM_INTEGRATIONS}`,
  pathPattern: `/${ROUTES.CYCLOPS}/${ROUTES.ERM_INTEGRATIONS}/*`,
  type: ROUTES.ERM_INTEGRATIONS,
  domains: ALL_DOMAINS,
  predicate: isErmIntegrationsAllowed
};
const createAlertIntegrationsTab = (type, title) => ({
  title,
  path: `/${ROUTES.INTEGRATIONS_HUB_TAB}/${type}`,
  pathPattern: `/${ROUTES.INTEGRATIONS_HUB_TAB}/${type}/*`,
  type,
  domains: ALL_DOMAINS,
  predicate: isErmIntegrationsAllowed
});
export const WEBHOOK_INTEGRATIONS = createAlertIntegrationsTab(
  ROUTES.ALERT_WEBHOOKS_CONFIG,
  t('common.integrations_hub_webhook_tab')
);
export const NOTIFICATIONS_INTEGRATIONS = createAlertIntegrationsTab(
  ROUTES.ALERT_CHAT_APP_NOTIFICATIONS,
  t('common.integrations_hub_notifications_tab')
);
export const AD_INTEGRATIONS = createAlertIntegrationsTab(
  ROUTES.ALERT_INTEGRATIONS_CONFIG,
  t('common.integrations_hub_ad_tab')
);
export const ERM_INTEGRATIONS_TABS = [
  ERM_INTEGRATIONS,
  WEBHOOK_INTEGRATIONS,
  NOTIFICATIONS_INTEGRATIONS,
  AD_INTEGRATIONS
];

export const INTEGRATIONS_HUB = {
  title: t('common.integrations'),
  path: `/${ROUTES.CYCLOPS}/${ROUTES.CYCLOPS_INTEGRATIONS}`,
  type: ROUTES.INTEGRATIONS_HUB_TAB,
  hideTenantSelection: true,
  domains: ALL_DOMAINS,
  subPages: [CYCLOPS_INTEGRATIONS, TEM_INTEGRATIONS, ASM_INTEGRATIONS, ...ERM_INTEGRATIONS_TABS],
  keywords: [...NavKeywords.temIntegrations, ...NavKeywords.cyclops],
  isVisible: ctx =>
    IS_USER_HAVE_ACCESS_TO_TAB.checkAccessToTab({ tab: CYCLOPS_INTEGRATIONS, ...ctx }) ||
    IS_USER_HAVE_ACCESS_TO_TAB.checkAccessToTab({ tab: ASM_INTEGRATIONS, ...ctx }) ||
    IS_USER_HAVE_ACCESS_TO_TAB.checkAccessToTab({ tab: TEM_INTEGRATIONS, ...ctx }) ||
    ERM_INTEGRATIONS_TABS.some(tab => IS_USER_HAVE_ACCESS_TO_TAB.checkAccessToTab({ tab, ...ctx }))
};

const EM_MENU = () => [
  { tab: OPERATIONAL_DASHBOARD },

  {
    title: t('common.action_center'),
    icon: Icons.Alert,
    keywords: NavKeywords.actionCenter,
    subMenu: [{ tab: ALERTS }, { tab: TEM_INSIGHTS }, { tab: CYCLOPS_SEARCH }]
  },

  {
    title: t('common.exposure'),
    icon: Icons.Shield,
    keywords: NavKeywords.exposure,
    subMenu: [{ tab: TEM_DASHBOARD }, { tab: ASM_REVAMP }, { tab: TEM_REMEDIATIONS }, { tab: CYCLOPS_INTERNAL_ASM }]
  },

  {
    title: t('common.remediations'),
    icon: Icons.Remediation,
    keywords: NavKeywords.remediations,
    subMenu: [{ tab: AUTOMATED_TAKEDOWN }, { tab: CREDENTIAL_PURCHASED }, { tab: INVESTIGATIONS }]
  },

  {
    title: t('common.cyber_threat_intelligence'),
    icon: Icons.ShieldTarget,
    keywords: NavKeywords.cyberThreatIntelligence,
    subMenu: [
      { tab: THREAT_LANDSCAPE },
      { tab: IOC_MGMT },
      { tab: KNOWLEDGE_HUB },
      { tab: CYBER_NEWS_ATTACKS },
      { tab: REPORTS },
      { tab: TEM_INDICATORS }
    ]
  },

  {
    title: t('common.targeted_intelligence'),
    icon: Icons.ThreatIntelNg,
    keywords: NavKeywords.targetedIntelligence,
    subMenu: [
      { tab: LEAKED_CREDENTIALS },
      { tab: VENDOR_INVENTORY },
      { tab: INBOX },
      { tab: LOOKALIKE_DOMAIN },
      { tab: SOCIAL_MEDIA_FAKE_PAGES },
      { tab: TEM_MAP }
    ]
  },

  {
    title: t('common.threat_investigation'),
    icon: Icons.ThreatInvestigation,
    keywords: NavKeywords.threatInvestigation,
    subMenu: [{ tab: FORENSICS }, { tab: INTELLIGENCE_ARCHIVE }, { tab: THREAT_EMULATION }]
  },

  {
    title: t('common.reports_dashboards'),
    icon: Icons.Dashboard,
    keywords: NavKeywords.reportsDashboards,
    subMenu: [
      { tab: RISK_DASHBOARD },
      { tab: EXECUTIVE_DASHBOARD },
      { tab: ENV_SUMMARY },
      { tab: CYCLOPS_DASHBOARDS },
      { tab: THREAT_READINESS_DASHBOARD }
    ]
  },
  {
    title: t('common.integrations'),
    icon: Icons.Chain,
    keywords: NavKeywords.integrationsGroup,
    subMenu: [{ tab: INTEGRATIONS_HUB }, { tab: TEM_ASSETS }]
  },
  {
    title: t('common.blast'),
    icon: Icons.Blast,
    keywords: NavKeywords.blast,
    subMenu: [{ tab: BLAST_TAB }, { tab: BLAST_SCANS_TAB }]
  },

  { tab: { ...CUSTOMER_MGMT, icon: Icons.Tenants } },

  {
    title: t('common.settings'),
    icon: Icons.Gear,
    keywords: NavKeywords.settingsGroup,
    subMenu: [
      { tab: { ...MY_ACCOUNT, icon: undefined } },
      { tab: TEM_SETTINGS },
      { tab: CYCLOPS_CONFIGURATION },
      { tab: NOTIFICATIONS_MANAGER }
    ]
  }
];

const resolveTab = (tab, { user, flags }) => {
  if (!tab) return tab;
  let resolved = tab;
  if (typeof resolved.badgeType === 'function') {
    resolved = { ...resolved, badgeType: resolved.badgeType({ user, flags }) };
  }
  return resolved;
};

// later isTemOnlyMenu will be moved to true if solution is adopted
const filterMenu = ({ menu, user, selectedTenant, customers, permissions, flags, navMenuFeatures }) =>
  menu
    ?.map(item => {
      const { tab } = item;
      const hasAccess =
        !tab ||
        IS_USER_HAVE_ACCESS_TO_TAB.checkAccessToTab({
          tab,
          user,
          selectedTenant,
          customers,
          permissions,
          flags,
          navMenuFeatures
        });
      if (!hasAccess) return false;
      const resolvedTab = resolveTab(tab, { user, flags, selectedTenant });
      if (!item.subMenu || item.subMenu.length === 0) return resolvedTab !== tab ? { ...item, tab: resolvedTab } : item;
      const subMenu = filterMenu({
        menu: item.subMenu,
        user,
        selectedTenant,
        customers,
        permissions,
        flags,
        navMenuFeatures
      });
      // Promote single-child groups: hoist the sole remaining child to top-level,
      // inheriting the group's icon (e.g. cyberintOnly Action Center → ALERTS top-level).
      if (subMenu.length === 1 && subMenu[0].tab) {
        return { tab: resolveTab({ ...subMenu[0].tab, icon: item.icon }, { user, flags, selectedTenant }) };
      }
      if (subMenu.length > 0) {
        return { ...item, subMenu };
      }
      /**
       * Reaching here means the item have all subMenu filtered out:
       * if the parent route doesn't have a page/tab, it will be filtered out too, otherwise (have page - not simple menu parent) it will be visible.
       * (Such cases will happen in the future - for example risk will have some kind of summary page.)
       */
      return resolvedTab ? { ...item, tab: resolvedTab, subMenu } : false;
    })
    .filter(item => !!item); // remove falsy values

export const getMenuItems = ({ user, selectedTenant, customers, permissions, flags, menu, navMenuFeatures }) => {
  menu = menu || EM_MENU({ flags });
  return filterMenu({ menu, user, selectedTenant, customers, permissions, flags, navMenuFeatures });
};

export const extractTabsFromMenu = menuItems => {
  const tabs = menuItems.reduce((arr, item) => {
    const subTabs = item.subMenu ? extractTabsFromMenu(item.subMenu) : [];
    return item.tab ? [...arr, item.tab, ...subTabs] : [...arr, ...subTabs];
  }, []);
  return tabs;
};
